import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcMovementPriorityDto {
    id: number;
    companyNo: string;
    movementTypeNo: number;
    binTypeNo: number;
    fromPriority: number;
    toPriority: number;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcMovementPriorityRequest {
    companyNo: string;
    movementTypeNo: number;
    binTypeNo: number;
    fromPriority: number;
    toPriority: number;
}

export interface UpdateStcMovementPriorityRequest {
    fromPriority: number;
    toPriority: number;
}

export interface BulkUpdateStcMovementPriorityRequest {
    companyNo: string;
    movementTypeNo: number;
    binTypeNo: number;
    fromPriority: number;
    toPriority: number;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

class StcMovementPriorityService {
    private baseUrl = `${API_BASE_URL}/stc-movement-priorities`;

    async getByCompanyMovementTypeAndBinType(companyNo: string, movementTypeNo: number, binTypeNo: number): Promise<StcMovementPriorityDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${movementTypeNo}/${binTypeNo}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    async getByCompanyAndMovementType(companyNo: string, movementTypeNo: number): Promise<StcMovementPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}/movement-type/${movementTypeNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByCompanyNo(companyNo: string): Promise<StcMovementPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getCurrent(): Promise<StcMovementPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<StcMovementPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateStcMovementPriorityRequest): Promise<StcMovementPriorityDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async update(companyNo: string, movementTypeNo: number, binTypeNo: number, request: UpdateStcMovementPriorityRequest): Promise<StcMovementPriorityDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${movementTypeNo}/${binTypeNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateStcMovementPriorityRequest[]): Promise<StcMovementPriorityDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async delete(companyNo: string, movementTypeNo: number, binTypeNo: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${movementTypeNo}/${binTypeNo}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
    }

    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async exists(companyNo: string, movementTypeNo: number, binTypeNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${movementTypeNo}/${binTypeNo}`);
            return response.ok;
        } catch (error: any) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<StcMovementPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }
}

export const stcMovementPriorityService = new StcMovementPriorityService();
